import { Logger } from '@/utils/logger';
import type { ContactDetailsStates, KeyOnlyStates, ResendInviteStates } from '@montugroup/circuit-api-contracts';
import { formatEscriptToken } from '@montugroup/circuit-shared';
import { getTsCustomerPortalClient } from '@montugroup/circuit-shared/api/customer-portal.client';
import type { z } from 'zod';

const logger = new Logger('useBeginEscriptSignup');

const client = getTsCustomerPortalClient();

export type TKeyOnlyStates = z.infer<typeof KeyOnlyStates>;
export type TResendInviteStates = z.infer<typeof ResendInviteStates>;
export type TContactDetailsStates = z.infer<typeof ContactDetailsStates>;

type MutationResult = ReturnType<typeof client.beginEscriptSignup.useMutation>;
type MutateAsync = MutationResult['mutateAsync'];
export type TBeginEscriptSignupResponse = Awaited<ReturnType<MutateAsync>>['body']['data']['result'];
export type TKeyOnlyResponse = Extract<TBeginEscriptSignupResponse, { onboardState: TKeyOnlyStates }>;
export type TResendInviteResponse = Extract<TBeginEscriptSignupResponse, { onboardState: TResendInviteStates }>;
export type TContactDetailsResponse = Extract<TBeginEscriptSignupResponse, { onboardState: TContactDetailsStates }>;
export type TBeginEscriptSignupErrorResponse = MutationResult['error'];

/**
 * useBeginEscriptSignup handles processing of a token submitted by a potential customer in the updated pre onboarding escript upload flow.
 *
 * @returns beginEscriptSignupResponse - response data, beginEscriptSignupMutate - mutate function, isPending - boolean
 */
export function useBeginEscriptSignup() {
  const { mutate, isPending } = client.beginEscriptSignup.useMutation();

  const beginEscriptSignupMutate = (
    token: string,
    onSuccessCallback: (data: TBeginEscriptSignupResponse) => void,
    onErrorCallback?: () => void,
  ) =>
    mutate(
      {
        body: {
          data: {
            token: formatEscriptToken(token),
          },
        },
      },
      {
        onSuccess: (data) => {
          const responseData = data.body.data.result;
          logger.info('pre onboarding token successfully processed via beginEscriptSignup contract', responseData);
          onSuccessCallback(responseData);
        },
        onError: (error: TBeginEscriptSignupErrorResponse) => {
          logger.error('Error submitting pre onboarding token via beginEscriptSignup contract: ' + error);
          onErrorCallback && onErrorCallback();
        },
      },
    );

  return {
    beginEscriptSignupMutate,
    isPending,
  };
}
